import { queryConversation, QueryConversationParams } from 'services/api/conversation';
import { sbsQueryConversation } from 'services/api/sbs';
import { useBatchUpdateMessages, useMessageList } from '../message';
import { useCurrentChatPreferenceValue, useSetCurrentChatPreference } from './chatPreference';
import type { newCancelTokenFn } from 'hooks/useCancelToken';
import { useCallback, useMemo, useState } from 'react';

import { useChatConversationContext } from 'views/Conversation/Chat/contexts/ChatConversationContext';
import { useOrgConfigStateValue } from '../useOrgConfig';
import { useReceptionConfig } from 'views/Setting/ConversationSetting/useReceptionConfig';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import type { LocalMessage } from '../conversation';

export const messageTypes: Conversation.MessageType[] = [
  'TEXT',
  'STICKER',
  'IMAGE',
  'VIDEO',
  'TEXT_VIDEO',
  'ORDER',
  'ITEM',
  'ITEM_LIST',
  'VOUCHER',
  'OFFER',
  'LAZADA_OFFER',
  'BUNDLE_MESSAGE',
  'NOTIFICATION',
  'NONSUPPORT',
  'FAQ_LIVEAGENT',
  'SYS_TRANSFER',
  'SYS_ASSIGN',
  'SYS_REMIND_TRIGGER',
  'SYS_REMIND_MANUAL_CLOSE',
  'SYS_REMIND_AUTO_CLOSE',
  'SYS_WA_REJECT',
  'SYS_ASTERISK_STAR',
  'FOLLOWER',
  'REFUND',
  'TEMPLATE',
  'IMAGE_WITH_TEXT',
  'SYS_BLACKLIST',
  'SYS_REMIND_SOLVE',
  'SYS_OUT',
  'SYS_PAUSE_AI_START',
  'SYS_PAUSE_AI_STOP',
  'SYS_PAUSE_AI_EXPIRE',
  'SYS_PAUSE_MK_RR_START',
  'SYS_PAUSE_MK_RR_STOP',
  'SYS_PAUSE_MK_RR_EXPIRE',
  'SYS_AUTO_CANCEL_ORDER',
  'SYS_NEG_RATE_PAUSE_AI_START',
  'SYS_NEG_RATE_PAUSE_AI_PROCESS',
  'SYS_INIT_CONVERSATION',
  'SBS_TRADE_INFO',
  'SYS_PROCESS_CONVERSATION',
  'SYS_CSAT',
  'SYS_FAQ_QUESTION',
  'SYS_NEG_RATE_PAUSE_AI_EXPIRE',
  'SYS_USER_ENTER_FROM_ITEM',
  'SYS_USER_ENTER_FROM_ORDER',
  'SYS_USER_ENTER_FROM_TRANSFER',
  'SYS_ALLOCATED_SERVICE',
  'SYS_IGNORE_BOT_SENT_LIMIT_REACHED',
  'SYS_MANUAL_MARK_SALES_STATUS',
  'SYS_AUTO_MARK_SALES_STATUS',
  'SYS_FORCE_ACQUIRE_CONTROL',
  'SYS_CLOSE_CONVERSATION',
  'SYS_RE_OPEN_CONVERSATION',
  'SYS_ASSIGN_CONTEXT',
  'SYS_NEG_RATE',
  'SYS_CANCEL_ORDER',
  'ATTACHMENT',
  'KW_NOTICE',
  'KW_PROLOGUE',
  'KW_FAQ',
  'KW_REMIND_ORDER',
  'KW_REMIND_PAYMENT',
  'KW_EVALUATION',
  'RETURN_REFUND_CARD',
  'SHOPEE_RETURN_REFUND_CARD',
  'FAQ_UNSUPPORTED',
  'NEW_FAQ',
  'FAQ_CATEGORY_CHOICE',
  'FAQ_LIVEAGENT_PROMPT',
  'FAQ_FEEDBACK_PROMPT',
  'FAQ_FEEDBACK',
  'FAQ_QUESTION_LIST',
  'FAQ_QUESTION',
  'SHOPEE_RICH_TEXT',
  'FAQ_BOT_REQUEST_TEXT',
  'FACEBOOK_MESSAGE',
  'TTS_LOGISTICS_CARD',
  'VARIATION_CARD',
  'FIRST_MSG_ITEM_RCMD',
  'JL_AI_GUIDE',
  'JL_ATTACHMENT',
  'JL_FIXED_REPLY',
  'JL_FORM',
  'JL_SYSTEM_MSG',
];

const getNouseMessageType = (
  msg: Conversation.Message,
): Conversation.NouseMessageType | undefined => {
  const { msgFromType, isFromOfficial, isAutoReply, botReplyType, extra } = msg;

  if (msgFromType !== 'ASSISTANT' && msgFromType !== 'BOT') {
    return undefined;
  }

  if (isFromOfficial && isAutoReply) {
    if (isTiKTokUpperCase(msg.channel) && msg.messageType === 'NONSUPPORT') {
      return undefined;
    }
    if (msg.channel === 'JOLECT') {
      return undefined;
    }
    return 'SELLER_CENTER_AUTO_REPLY';
  }

  if (botReplyType === 'WELCOME') {
    return 'WELCOME_REPLY';
  }

  if (botReplyType === 'TIMEOUT_AUTO_REPLY') {
    return 'TIMEOUT_AUTO_REPLY';
  }

  if (botReplyType === 'GUARANTEED_ANSWER') {
    return 'WATCH_LIMIT';
  }

  if (botReplyType === 'MARKETING') {
    const { botMarketingType } =
      (() => {
        if (!extra) {
          return undefined;
        }
        try {
          const extraInfo: Conversation.ExtraInfo = JSON.parse(extra);
          return extraInfo;
        } catch (_) {
          return undefined;
        }
      })() || {};

    if (!botMarketingType || botMarketingType === 'BMT_UNKNOWN') {
      return undefined;
    }

    const map: Partial<Record<typeof botMarketingType, Conversation.NouseMessageType>> = {
      BMT_ORDER_REMINDER: 'ORDER_REMINDER',
      BMT_PAYMENT_REMINDER: 'PAYMENT_REMINDER',
      BMT_PRAISE_REMINDER: 'PRAISE_REMINDER',
      BMT_CONFIRM_ORDER_REMINDER: 'LOGISTICS_CARE',
      BMT_DELAY_SHIP: 'LOGISTICS_CARE',
      BMT_SHIPPED_REMINDER: 'LOGISTICS_CARE',
      BMT_WAIT_FOR_TAX: 'LOGISTICS_CARE',
      BMT_DELIVERY_REMINDER: 'LOGISTICS_CARE',
      BMT_WAIT_DELIVERY_AGAIN: 'LOGISTICS_CARE',
      BMT_SIGN_REMINDER: 'LOGISTICS_CARE',
      BMT_GOOD_REVIEW_REMINDER: 'POSITIVE_REVIEW_CARE',
      BMT_INITIATIVE_TASK: 'PROACTIVELY_CONTACT_BUYER',
      BMT_TIKTOK_END_SESSION: 'TIKTOK_END_SESSION',
      BMT_CSAT_INVITE_REVIEW: 'CSAT_INVITE_REVIEW',
    };

    return map[botMarketingType];
  }
};

const getLastNouseMessage = (list: Conversation.NouseMessage[][]) => {
  if (list.length) {
    const last = list[list.length - 1];
    if (last.length) {
      return last[last.length - 1];
    }
  }
};

const useCurrentChat = (
  iframeMode: boolean = false,
  onMercadoChatView: boolean = false,
  enableNouseMessage = false,
) => {
  const preference = useCurrentChatPreferenceValue();
  const setPreference = useSetCurrentChatPreference();
  const messageList = useMessageList();

  const batchUpdateMessages = useBatchUpdateMessages();

  const { currentConversationId: conversationId, currentConversation: conversation } =
    useChatConversationContext();
  const { channel } = conversation;

  const [keyMsgId, setKeyMsgId] = useState<string>();

  const messages = useMemo(() => {
    // 美客多争议状态消息过滤
    const msgs =
      channel === 'MERCADO'
        ? messageList.filter((item) => !!item.withMercado === onMercadoChatView)
        : messageList;

    const res = msgs
      .filter((msg) => msg.conversationId === conversationId)
      .sort((a, b) => {
        const timeDiff = parseInt(a.sendTime as string, 10) - parseInt(b.sendTime as string, 10);

        if (timeDiff !== 0 || !(a.externalId && b.externalId)) {
          return timeDiff;
        }

        return parseInt(a.externalId, 10) - parseInt(b.externalId, 10);
      });

    if (channel !== 'FACEBOOK') {
      return res;
    }

    return res.reduce((res, msg) => {
      const {
        messageType,
        textContent = {
          originalText: '',
          translationContent: {},
        },
        fbMsgContent: { message, sticker, attachments: { data = [] } = {} } = {},
      } = msg;
      if (message) {
        res.push({
          ...msg,
          messageType: 'TEXT',
          textContent: {
            ...textContent,
            text: message,
          },
        });
        return res;
      }
      if (sticker) {
        res.push({
          ...msg,
          messageType: 'IMAGE',
          imageContent: {
            imgUrl: sticker,
            externalImgUrl: sticker,
            width: 0,
            height: 0,
          },
        });
        return res;
      }
      const [{ imageData, videoData } = {}] = data;
      if (imageData?.url) {
        const imgData = data.filter(({ imageData }) => imageData?.url);
        if (imgData.length > 1) {
          res.push(msg);
          return res;
        }
        res.push({
          ...msg,
          messageType: 'IMAGE',
          imageContent: {
            imgUrl: imageData.url,
            externalImgUrl: imageData.url,
            width: imageData.width,
            height: imageData.height,
          },
        });
        return res;
      }
      if (videoData?.url) {
        res.push({
          ...msg,
          messageType: 'VIDEO',
          videoContent: {
            videoUrl: videoData.url,
            localVideoUrl: videoData.url,
            thumbUrl: videoData.previewUrl,
            videoId: '',
          },
        });
        return res;
      }
      if (
        (messageType === 'TEXT' && msg.textContent) ||
        (messageType === 'IMAGE' && msg.imageContent) ||
        (messageType === 'VIDEO' && msg.videoContent)
      ) {
        res.push(msg);
      }
      return res;
    }, [] as LocalMessage[]);
  }, [conversationId, messageList, onMercadoChatView, channel]);

  const { whiteListConfig: { MESSAGE_SIMPLE_MODE } = {} } = useOrgConfigStateValue();
  const { simpleModeConfig: { simpleMode, simpleModeItems } = {} } = useReceptionConfig() || {};

  const getNouseMessageList = useCallback(
    (messages: Conversation.Message[], pre: Conversation.NouseMessage[][] = []) => {
      return messages.reduce((res, cur, index) => {
        if (cur.status !== 'SUCCESS') {
          return res;
        }
        const nouseMessageType = getNouseMessageType(cur);
        if (
          nouseMessageType === undefined ||
          !simpleModeItems?.find(({ type }) => type === nouseMessageType)?.checked ||
          res.some((item) => item.some(({ msgId }) => msgId === cur.msgId))
        ) {
          return res;
        }
        const item = {
          msgId: cur.msgId,
          type: nouseMessageType,
        };
        if (index > 0 && messages[index - 1].msgId === getLastNouseMessage(res)?.msgId) {
          res[res.length - 1] = [...res[res.length - 1], item];
          return [...res];
        } else {
          return [...res, [item]];
        }
      }, pre);
    },
    [simpleModeItems],
  );

  const [nouseMsgList, setNouseMsgList] = useState<Conversation.NouseMessage[][]>([]);

  const fetch = useCallback(
    async (
      params:
        | Omit<QueryConversationParams, 'conversation_id'>
        | Omit<QueryConversationParams, 'conversation_id'>[],
      newCancelToken: newCancelTokenFn,
    ) => {
      if (!conversationId) {
        return [];
      }
      const {
        data: { data: { message: data = [], keyMsgId } = {} },
      } = await (Array.isArray(params)
        ? Promise.all(
            params.map((params) => {
              return queryConversation(
                { ...params, conversation_id: conversationId },
                iframeMode,
                newCancelToken,
              );
            }),
          ).then((res) => {
            return {
              data: {
                data: {
                  message: res.map((data) => data.data.data.message || []).flat(),
                  keyMsgId: res[0].data.data.keyMsgId,
                },
              },
            };
          })
        : queryConversation(
            { ...params, conversation_id: conversationId },
            iframeMode,
            newCancelToken,
          ));
      if (keyMsgId && keyMsgId !== '0') {
        setKeyMsgId(keyMsgId);
      }
      const messageList = data.filter(({ messageType }) => messageTypes.includes(messageType));
      if (enableNouseMessage && MESSAGE_SIMPLE_MODE && simpleMode) {
        setNouseMsgList((pre) => getNouseMessageList(messageList, pre));
      }
      const start = performance.now();
      batchUpdateMessages(
        messageList.map((msg) => [
          msg.msgId,
          (preMsg) => {
            const localMsg: LocalMessage = preMsg
              ? msg.videoContent
                ? {
                    ...preMsg,
                    videoContent: msg.videoContent,
                  }
                : preMsg
              : msg; // 优先取本地消息。因为在接口返回期间可能发生消息修改，比如消息翻译成功。
            return localMsg;
          },
        ]),
      );
      const end = performance.now();
      console.log('updateMessage', end - start);

      return data;
    },
    [
      conversationId,
      iframeMode,
      enableNouseMessage,
      MESSAGE_SIMPLE_MODE,
      simpleMode,
      batchUpdateMessages,
      getNouseMessageList,
    ],
  );

  const fetchSbs = useCallback(
    async (params: Omit<QueryConversationParams, 'conversation_id'>) => {
      if (!conversationId) {
        return [];
      }
      const {
        data: { data: { message: data = [] } = {} },
      } = await sbsQueryConversation({
        ...params,
        conversation_id: conversationId,
        talkId: conversation?.talkId!,
      });

      batchUpdateMessages(data.map((msg) => [msg.msgId, msg]));

      return data;
    },
    [batchUpdateMessages, conversation?.talkId, conversationId],
  );

  return {
    messages,
    keyMsgId,
    conversation,
    fetch,
    fetchSbs,
    preference,
    setPreference,
    nouseMsgList,
  };
};

export default useCurrentChat;
